<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { LightBulbIcon } from '@heroicons/vue/outline';
import type { AuraInsight } from '@/types';
import AuraRichText from './AuraRichText.vue';

const props = defineProps<{
    insight: AuraInsight;
}>();
const { t } = useI18n();

const severity = computed(() => {
    const values = {
        warning: {
            label: t('aura.cards.insight.severity.warning'),
            color: '#F5B041',
            text: '#242B59',
        },
        negative: {
            label: t('aura.cards.insight.severity.negative'),
            color: '#E56B6F',
            text: '#FFFFFF',
        },
        positive: {
            label: t('aura.cards.insight.severity.positive'),
            color: '#48DDC3',
            text: '#242B59',
        },
        neutral: {
            label: t('aura.cards.insight.severity.neutral'),
            color: '#CBD5E1',
            text: '#242B59',
        },
    } as const;

    return values[props.insight.type] || values.neutral;
});
</script>

<template>
    <article
        class="relative box-border min-h-[250px] w-full max-w-none overflow-hidden rounded-2xl border border-[#E5E7EB] bg-[#F5F6F8] shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sm:max-w-[325px]"
    >
        <span
            class="absolute bottom-0 left-0 top-0 w-1.5"
            :style="{ backgroundColor: severity.color }"
        />

        <div class="flex flex-col items-start gap-3 px-5 pb-5 pt-4">
            <div
                class="flex h-8 w-8 items-center justify-center rounded-[14px] bg-white shadow-[0_0_0_1px_#E5E5E5,0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)]"
            >
                <LightBulbIcon class="h-4 w-4 text-[#242B59]" aria-hidden="true" />
            </div>

            <div class="w-full space-y-2">
                <div class="flex items-center gap-2">
                    <h3 class="text-sm font-bold uppercase tracking-[0.7px] text-[#242B59]">
                        {{ $t('aura.cards.insight.title') }}
                    </h3>
                    <span
                        class="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.55px]"
                        :style="{ backgroundColor: severity.color, color: severity.text }"
                    >
                        {{ severity.label }}
                    </span>
                </div>

                <AuraRichText
                    class="text-sm font-medium leading-[23px] text-[#626468]"
                    :text="insight.text"
                />
            </div>
        </div>
    </article>
</template>
